import React, { Suspense, lazy } from "react";
import { saveResumeState } from "@/lib/pwaResumeState";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { RoleProvider } from "@/contexts/RoleContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";

import { ModoForjaProvider } from "@/contexts/ModoForjaContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AppReadyGate } from "@/components/AppReadyGate";
import { CenteredSpinner } from "@/components/ui/CenteredSpinner";

import ModoForjaOverlay from "@/components/modoforja/ModoForjaOverlay";
import ModoForjaAudioManager from "@/components/modoforja/ModoForjaAudioManager";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { PWADebugPanel } from "@/components/PWADebugPanel";
import { BannedUserModal } from "@/components/BannedUserModal";
import { SessionRevokedModal } from "@/components/SessionRevokedModal";
import { MultiLocationWarningModal } from "@/components/MultiLocationWarningModal";
import { OnboardingGate } from "@/components/OnboardingGate";

import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { useUserActivity } from "@/hooks/useUserActivity";
import { useSessionGuard } from "@/hooks/useSessionGuard";

// Critical routes - load immediately
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";

// Component to check banned status
function BannedCheck({ children }: { children: React.ReactNode }) {
  const { isBanned, banReason, signOut, accessState, refreshAccess } = useAuth();
  useUserActivity();
  const { revoked, warningEvent, acknowledgeWarning } = useSessionGuard();

  if (isBanned) {
    return (
      <BannedUserModal
        reason={banReason}
        onLogout={signOut}
        onRetry={refreshAccess}
        verificationError={accessState === 'error'}
      />
    );
  }

  if (revoked) {
    return <SessionRevokedModal onContinue={() => { window.location.href = '/auth'; }} />;
  }

  return (
    <>
      {children}
      {warningEvent && (
        <MultiLocationWarningModal
          details={warningEvent.details}
          onAcknowledge={acknowledgeWarning}
        />
      )}
    </>
  );
}

// Resilient lazy loader: retries up to 3 times with progressive backoff.
// On final failure, forces a full page reload to bust stale chunks.
function lazyRetry(
  factory: () => Promise<{ default: React.ComponentType<any> }>,
  retries = 3,
  interval = 1000,
): React.LazyExoticComponent<React.ComponentType<any>> {
  return lazy(() =>
    factory().catch((error: unknown) => {
      if (retries <= 0) {
        // Guard against infinite reload loops. Only auto-reload once per
        // 60s window; otherwise render an inline fallback so the user can
        // recover manually without losing state on every tab switch.
        const GUARD_KEY = 'lazy-retry-reload-ts';
        const COOLDOWN_MS = 60_000;
        let canReload = true;
        try {
          const last = parseInt(sessionStorage.getItem(GUARD_KEY) || '0', 10);
          if (Date.now() - last < COOLDOWN_MS) {
            canReload = false;
          } else {
            sessionStorage.setItem(GUARD_KEY, String(Date.now()));
          }
        } catch { /* noop */ }

        if (canReload) {
          saveResumeState();
          window.location.reload();
          return new Promise<never>(() => {});
        }

        console.warn('[lazyRetry] Chunk load failed and reload cooldown active — rendering fallback', error);
        const Fallback: React.ComponentType<any> = () => (
          <div className="min-h-[50vh] flex items-center justify-center p-6">
            <div className="text-center max-w-sm space-y-4">
              <p className="text-sm text-muted-foreground">
                Não foi possível carregar esta tela. Verifique sua conexão e tente novamente.
              </p>
              <button
                onClick={() => {
                  try { sessionStorage.removeItem('lazy-retry-reload-ts'); } catch {}
                  saveResumeState();
                  window.location.reload();
                }}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
              >
                Recarregar
              </button>
            </div>
          </div>
        );
        return { default: Fallback };
      }
      return new Promise<{ default: React.ComponentType<any> }>((resolve) =>
        setTimeout(
          () => resolve(lazyRetry(factory, retries - 1, interval + 1000) as any),
          interval,
        ),
      );
    }),
  );
}

// Lazy loaded routes - load on demand
const MinhaForja = lazyRetry(() => import("@/pages/MinhaForja"));
const Marketplace = lazyRetry(() => import("@/pages/Marketplace"));
const ListingDetails = lazyRetry(() => import("@/pages/marketplace/ListingDetails"));
const NegotiationChat = lazyRetry(() => import("@/pages/marketplace/NegotiationChat"));
const Negotiations = lazyRetry(() => import("@/pages/marketplace/Negotiations"));

const SellerPlans = lazyRetry(() => import("@/pages/marketplace/SellerPlans"));
const SellerRules = lazyRetry(() => import("@/pages/marketplace/SellerRules"));
const SellerVerification = lazyRetry(() => import("@/pages/marketplace/SellerVerification"));
const HowItWorks = lazyRetry(() => import("@/pages/marketplace/HowItWorks"));
const MyMarketplace = lazyRetry(() => import("@/pages/marketplace/MyMarketplace"));
const CreditRecharge = lazyRetry(() => import("@/pages/marketplace/CreditRecharge"));
const CreditRechargePayment = lazyRetry(() => import("@/pages/marketplace/CreditRechargePayment"));
const PaymentHistory = lazyRetry(() => import("@/pages/marketplace/PaymentHistory"));
const ManagePlan = lazyRetry(() => import("@/pages/marketplace/ManagePlan"));
const PlanChangePayment = lazyRetry(() => import("@/pages/marketplace/PlanChangePayment"));
const CommandTerminal = lazyRetry(() => import("@/pages/CommandTerminal"));
const StorageOptimizer = lazyRetry(() => import("@/pages/admin/StorageOptimizer"));
const Profile = lazyRetry(() => import("@/pages/Profile"));
const Parceiros = lazyRetry(() => import("@/pages/Parceiros"));
const PartnerDetail = lazyRetry(() => import("@/pages/parceiros/PartnerDetail"));
const PartnerAdmin = lazyRetry(() => import("@/pages/parceiros/PartnerAdmin"));
const CampaignDetail = lazyRetry(() => import("@/pages/parceiros/CampaignDetail"));
const Arsenal = lazyRetry(() => import("@/pages/Arsenal"));
const Downloader = lazyRetry(() => import("@/pages/Downloader"));
const Transcription = lazyRetry(() => import("@/pages/Transcription"));
const GmvCreators = lazyRetry(() => import("@/pages/GmvCreators"));
const Comunidade = lazyRetry(() => import("@/pages/Comunidade"));
const Notifications = lazyRetry(() => import("@/pages/Notifications"));
const NotFound = lazyRetry(() => import("@/pages/NotFound"));
const LoginOTP = lazyRetry(() => import("@/pages/LoginOTP"));
const TermosDeUso = lazyRetry(() => import("@/pages/TermosDeUso"));
const Privacidade = lazyRetry(() => import("@/pages/Privacidade"));
const WelcomeLanding = lazyRetry(() => import("@/pages/welcome/WelcomeLanding"));
const WelcomeStudent = lazyRetry(() => import("@/pages/welcome/WelcomeStudent"));
const WelcomeVisitor = lazyRetry(() => import("@/pages/welcome/WelcomeVisitor"));
// Optimized QueryClient with better caching and retry logic
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

const PageLoader = () => <CenteredSpinner fullScreen />;

/**
 * Global idle-time prefetch: downloads JS chunks for all main routes
 * while the browser is idle, so subsequent navigations feel instant.
 * Tier 1 (1.5s): most visited routes. Tier 2 (3.5s): secondary routes.
 */
function useGlobalRoutePrefetch() {
  const hasPrefetched = React.useRef(false);
  React.useEffect(() => {
    if (hasPrefetched.current) return;
    hasPrefetched.current = true;

    const schedule = (fn: () => void, delay: number) =>
      setTimeout(() => {
        if ('requestIdleCallback' in window) {
          (window as Window).requestIdleCallback(fn, { timeout: 15000 });
        } else {
          fn();
        }
      }, delay);

    const t1 = schedule(() => {
      import("@/pages/Comunidade");
      import("@/pages/CommandTerminal");
      import("@/pages/MinhaForja");
      import("@/pages/Marketplace");
      import("@/pages/Profile");
    }, 1500);

    const t2 = schedule(() => {
      import("@/pages/Parceiros");
      import("@/pages/Arsenal");
      import("@/pages/Downloader");
      import("@/pages/Transcription");
      import("@/pages/GmvCreators");
      import("@/pages/Notifications");
    }, 3500);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
}

function AppShell({ children }: { children: React.ReactNode }) {
  useGlobalRoutePrefetch();
  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <BrandingProvider>
          <RoleProvider>
              <ModoForjaProvider>
                <ErrorBoundary>
                  <AppShell>
                  <Sonner />
                  <ModoForjaAudioManager />
                  <ModoForjaOverlay />
                   <PWAInstallPrompt />
                   {typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('pwa-debug') && <PWADebugPanel />}
                  <BrowserRouter>
                    <ScrollToTop />
                    <Routes>
                      <Route
                        path="*"
                        element={
                          <AppReadyGate>
                      <BannedCheck>
                        <OnboardingGate>
                          <Routes>
                          <Route path="/auth" element={<Suspense fallback={<PageLoader />}><Auth /></Suspense>} />
                            <Route path="/login2" element={<Suspense fallback={<PageLoader />}><LoginOTP /></Suspense>} />
                            <Route path="/welcome" element={<Suspense fallback={<PageLoader />}><WelcomeLanding /></Suspense>} />
                            <Route path="/welcome/aluno" element={<Suspense fallback={<PageLoader />}><WelcomeStudent /></Suspense>} />
                            <Route path="/welcome/visitante" element={<Suspense fallback={<PageLoader />}><WelcomeVisitor /></Suspense>} />
                            <Route path="/termos-de-uso" element={<Suspense fallback={<PageLoader />}><TermosDeUso /></Suspense>} />
                            <Route path="/privacidade" element={<Suspense fallback={<PageLoader />}><Privacidade /></Suspense>} />
                            {/* Single Layout instance — navbar never remounts */}
                            <Route element={<Layout />}>
                              {/* Public marketplace routes */}
                              <Route path="/marketplace" element={<Marketplace />} />
                              <Route path="/marketplace/:id" element={<ListingDetails />} />
                              {/* Protected routes */}
                              <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
                              <Route path="/minha-forja" element={<ProtectedRoute><MinhaForja /></ProtectedRoute>} />
                              
                              <Route path="/marketplace/negotiations" element={<ProtectedRoute><Negotiations /></ProtectedRoute>} />
                              <Route path="/marketplace/negotiations/:id" element={<ProtectedRoute><Negotiations /></ProtectedRoute>} />
                              <Route path="/marketplace/regras-do-vendedor" element={<ProtectedRoute><SellerRules /></ProtectedRoute>} />
                              <Route path="/marketplace/become-seller" element={<ProtectedRoute><SellerVerification /></ProtectedRoute>} />
                              <Route path="/marketplace/como-funciona" element={<ProtectedRoute><HowItWorks /></ProtectedRoute>} />
                              <Route path="/marketplace/me" element={<ProtectedRoute><MyMarketplace /></ProtectedRoute>} />
                              <Route path="/marketplace/recharge" element={<ProtectedRoute><CreditRecharge /></ProtectedRoute>} />
                              <Route path="/marketplace/recharge-payment" element={<ProtectedRoute><CreditRechargePayment /></ProtectedRoute>} />
                              <Route path="/marketplace/payments/:id?" element={<ProtectedRoute><PaymentHistory /></ProtectedRoute>} />
                              <Route path="/marketplace/plans" element={<ProtectedRoute><SellerPlans /></ProtectedRoute>} />
                              <Route path="/marketplace/manage-plan" element={<ProtectedRoute><ManagePlan /></ProtectedRoute>} />
                              <Route path="/marketplace/plan-payment" element={<ProtectedRoute><PlanChangePayment /></ProtectedRoute>} />
                              <Route path="/marketplace/negotiation/:id" element={<ProtectedRoute><NegotiationChat /></ProtectedRoute>} />
                              
                              <Route path="/parceiros" element={<ProtectedRoute><Parceiros /></ProtectedRoute>} />
                              <Route path="/parceiros/:slug" element={<ProtectedRoute><PartnerDetail /></ProtectedRoute>} />
                              <Route path="/parceiros/:slug/admin" element={<ProtectedRoute><PartnerAdmin /></ProtectedRoute>} />
                              <Route path="/parceiros/:slug/campanha/:campaignId" element={<ProtectedRoute><CampaignDetail /></ProtectedRoute>} />
                              <Route path="/command-terminal" element={<ProtectedRoute><CommandTerminal /></ProtectedRoute>} />
                              <Route path="/admin/storage-optimizer" element={<ProtectedRoute><StorageOptimizer /></ProtectedRoute>} />
                              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                              <Route path="/arsenal" element={<ProtectedRoute><Arsenal /></ProtectedRoute>} />
                              <Route path="/downloader" element={<ProtectedRoute><Downloader /></ProtectedRoute>} />
                              <Route path="/transcription" element={<ProtectedRoute><Transcription /></ProtectedRoute>} />
                              <Route path="/gmv-creators" element={<ProtectedRoute><GmvCreators /></ProtectedRoute>} />
                              <Route path="/comunidade" element={<ProtectedRoute><Comunidade /></ProtectedRoute>} />
                              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                            </Route>
                            <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
                          </Routes>
                        </OnboardingGate>
                      </BannedCheck>
                          </AppReadyGate>
                        }
                      />
                    </Routes>
                  </BrowserRouter>
                  </AppShell>
                </ErrorBoundary>
              </ModoForjaProvider>
          </RoleProvider>
        </BrandingProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
